// ─── Which paths the app host actually serves ────────────────────────────────
//
// `app.wefixtrades.com` is a PUBLIC TOOL PORTAL, not a second copy of the site.
// It serves the tools hub and the tool pages themselves. Everything else — the
// marketing pages, the authenticated portal, admin, auth — belongs to
// `wefixtrades.com`, and a request for one of those on the app host is
// permanently redirected to the same path on the marketing host, so every page
// has exactly ONE canonical home.
//
// This list lives in `shared/` because two very different consumers need the
// same answer and must never disagree:
//
//   * server/middleware/appHost.ts — decides whether to 301 to marketing.
//   * client/src/site/toolPortal.ts — decides whether to mount the rail.
//
// AN ALLOWLIST, NOT A DENYLIST.
// A denylist ("redirect anything under /pricing, /about, /solutions") silently
// leaks every NEW marketing page onto the app host the day it is added, where
// it would sit as a duplicate of the marketing copy. With an allowlist the
// failure mode is inverted and harmless: a newly added tool is simply not
// served from the app host until it is listed here, and the redirect sends the
// visitor to the working marketing copy.
//
// DRIFT IS GUARDED. `shared/appHostRoutes.test.ts` asserts this list is exactly
// the set of public (non-gated) destinations in TOOL_PORTAL_GROUPS plus the two
// hub paths, so adding a tool to the rail without serving it here — or the
// reverse — fails the check rather than shipping a redirect loop to nowhere.

/**
 * The tools hub. On the app host, `/` is the hub index rather than the
 * marketing home page (client/src/App.tsx RootRoute).
 */
export const APP_HOST_HUB_PATH = "/";

/**
 * Every path the app host serves, exactly. Query strings and trailing slashes
 * are normalized away before the lookup — see `isAppHostServedPath`.
 *
 * Gated `/portal/free-tools/*` widgets are deliberately ABSENT. The session
 * cookie is host-scoped (no `domain` is set on it in server/index.ts), so a
 * login on wefixtrades.com is simply not sent to app.wefixtrades.com and those
 * pages could never authenticate here. The hub still surfaces all 17 of them,
 * shown locked and pointing at the marketing host where the session lives —
 * which also keeps the app host's "no login, no account" promise literally
 * true for everything it serves.
 */
export const APP_HOST_PATHS: readonly string[] = [
  APP_HOST_HUB_PATH,
  "/free-tools",

  // Get found
  "/tools/free-audit",
  "/tools/local-serp-checker",
  "/tools/local-rank-tracker",
  "/tools/local-rank-grid",
  "/tools/local-rankflux",
  "/mapguard-suite",

  // Get listed
  "/tools/citation-checker",
  "/tools/nap-checker",
  "/citation-tracker",
  "/citation-builder",

  // Reviews & reputation
  "/tools/google-review-link-generator",
  "/tools/review-response-generator",

  // Quote & book
  "/products/quickquotepro/demo",
  "/products/quickquotepro/build-with-ai",

  // Content & widgets
  "/tools/gbp-post-generator",
  "/contentflow",
  "/tools/plumbing-ai-content-prompts",
  "/tools/hvac-ai-content-prompts",
  "/tools/electrical-ai-content-prompts",
  "/tools/roofing-ai-content-prompts",
  "/tools/landscaping-ai-content-prompts",
];

const APP_HOST_PATH_SET: ReadonlySet<string> = new Set(APP_HOST_PATHS);

/** Strip query + hash and any trailing slash, but keep a bare "/". */
export function normalizeAppHostPath(path: string): string {
  if (!path) return "/";
  const clean = path.split(/[?#]/)[0].replace(/(.)\/+$/, "$1");
  return clean || "/";
}

/** True when the app host serves this path itself (rather than redirecting). */
export function isAppHostServedPath(path: string): boolean {
  return APP_HOST_PATH_SET.has(normalizeAppHostPath(path));
}
